import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { date } from '@ipfabric/utils';
import { List } from 'immutable';
import get from 'lodash/get';
import { getSnapshotSize } from 'pages/SnapshotManagement/getSnapshotSize';
import PropTypes from 'prop-types';
import { pipe } from 'ramda';
import { Fragment, PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withAppProps } from 'utils/react/withAppProps';
import { CloudLicensingGuideGate } from './CloudLicensingGuide/CloudLicensingGuideGate';
import { LicenseUsage } from './LicenseUsage/LicenseUsage';
import Name from './Name';
import Note from './Note';
import { isSnapshotLoaded } from './helpers';
import { makeSelectContentSnapshot } from './selectors';

@withAppProps
class ContentDetail extends PureComponent {
  static propTypes = {
    msg: PropTypes.object.isRequired,
    onAddLabel: PropTypes.func.isRequired,
    selectedSite: ImmutablePropTypes.list.isRequired,
    snapshot: ImmutablePropTypes.map.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    snapshotStatus: PropTypes.string.isRequired,
    canEdit: PropTypes.bool,
  };

  getSites = () => {
    const { msg, selectedSite, snapshot, onAddLabel } = this.props;
    const sites = snapshot
      .get('sites', new List())
      .sort((a, b) =>
        a.get('siteName', '').localeCompare(b.get('siteName', '')),
      )
      .map((site) => (
        <Badge
          key={site.get('uid')}
          interactive
          color={
            selectedSite.some((s) => s.get('uid') === site.get('uid'))
              ? 'info'
              : 'neutral'
          }
          data-id={site.get('uid')}
          data-name={site.get('siteName')}
          onClick={onAddLabel}
        >
          {site.get('siteName')}
        </Badge>
      ));
    return (
      <div className='mb-5'>
        <div>
          <label htmlFor={undefined}>{get(msg, 'detail.sites')}</label>
          {!sites.size ? (
            <Tooltip content={get(msg, 'noSitesFound')}>
              <i className='ml-lgu-2 fa fa-ban' />
            </Tooltip>
          ) : null}
        </div>
        {!!sites.size && (
          <ScrollView
            autoHeight
            autoHeightMax={122}
            contentClassName='flex flex-row flex-wrap gap-1'
          >
            {sites.toJS()}
          </ScrollView>
        )}
      </div>
    );
  };

  formatDuration = (duration) => {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration - hours * 3600) / 60);
    const seconds = duration - hours * 3600 - minutes * 60;
    return pipe(
      (time) => {
        if (hours !== 0) {
          return `${hours < 10 ? `0${hours}` : hours}h`;
        }
        return time;
      },
      (time) => {
        if (minutes !== 0 || time !== '') {
          const displayMinutes = minutes < 10 ? `0${minutes}` : String(minutes);
          if (time === '') {
            return `${displayMinutes}m`;
          }
          return `${time}:${displayMinutes}m`;
        }
        return time;
      },
      (time) => {
        if (time === '') {
          return `${seconds}s`;
        }
        return `${time}:${seconds < 10 ? `0${seconds}` : String(seconds)}s`;
      },
    )('');
  };

  render() {
    const { msg, dateTimeFormat, timezone, snapshotStatus, canEdit, snapshot } =
      this.props;
    if (!snapshot) {
      return null;
    }
    const isCloudLicenseActive =
      snapshot.get('licenseUsage') !== null &&
      snapshot.get('licenseUsage') !== undefined;
    return (
      <Fragment>
        <Name snapshot={snapshot} canEdit={canEdit} msg={msg} />
        {snapshot.get('tsStart') && snapshot.get('tsEnd') ? (
          <div>
            <div className='snapshots-content__labels-wrapper mb-5'>
              <div>
                <label htmlFor={undefined}>{get(msg, 'detail.duration')}</label>
              </div>
            </div>
            <div className='float-left mr-5'>
              <div className='snapshots-content__detail-cell'>
                {this.formatDuration(
                  Math.round(
                    (snapshot.get('tsEnd') - snapshot.get('tsStart')) / 1000,
                  ),
                )}
              </div>
            </div>
          </div>
        ) : null}
        <div>
          <div className='snapshots-content__labels-wrapper'>
            <div>
              <label htmlFor={undefined}>{get(msg, 'detail.creator')}</label>
            </div>
          </div>
          <div className='float-left mr-5'>
            <div className='snapshots-content__detail-cell'>
              {snapshot.get('creator') || (
                <i className='text-gray-800'>{get(msg, 'detail.unknown')}</i>
              )}
            </div>
          </div>
        </div>
        <div className='mb-lgu-6 flex flex-wrap'>
          <div>
            <div className='snapshots-content__labels-wrapper'>
              <div>
                <label htmlFor={undefined}>{get(msg, 'detail.version')}</label>
              </div>
              <div>
                <label htmlFor={undefined}>{get(msg, 'detail.time')}</label>
              </div>
            </div>
            <div className='float-left mr-5'>
              <div className='snapshots-content__detail-cell'>
                {snapshot.get('version')}{' '}
                {snapshot.get('initialVersion') && (
                  <span className='text-gray-800'>
                    ({get(msg, 'detail.createdOn')}{' '}
                    {snapshot.get('initialVersion')})
                  </span>
                )}
              </div>
              <div className='snapshots-content__detail-cell'>
                {date.formatUtcTimestampInTimeZone(
                  snapshot.get('tsStart'),
                  timezone,
                  dateTimeFormat,
                )}
              </div>
            </div>
          </div>
          <div>
            <div className='snapshots-content__labels-wrapper'>
              <div>
                <label htmlFor={undefined}>{get(msg, 'detail.status')}</label>
              </div>
              <div>
                <label htmlFor={undefined}>
                  {get(msg, 'detail.loadedSize')}
                </label>
              </div>
            </div>
            <div className='float-left mr-5'>
              <div className='snapshots-content__detail-cell'>
                {get(msg, ['status', snapshotStatus])}
              </div>
              <div className='snapshots-content__detail-cell'>
                {getSnapshotSize(msg, snapshot.get('loadedSize'))}
              </div>
            </div>
          </div>
          <div>
            <div className='snapshots-content__labels-wrapper'>
              <div>
                <label htmlFor={undefined}>
                  {isCloudLicenseActive
                    ? get(msg, 'detail.licenseUsage')
                    : get(msg, 'detail.devices')}
                </label>
              </div>
              <div>
                <label htmlFor={undefined}>
                  {get(msg, 'detail.unloadedSize')}
                </label>
              </div>
            </div>
            <div className='float-left mr-5'>
              {isCloudLicenseActive ? (
                <LicenseUsage
                  className='snapshots-content__detail-cell'
                  licenseUsage={snapshot.get('licenseUsage').toJS()}
                />
              ) : (
                <div className='snapshots-content__detail-cell'>
                  {(snapshot.get('totalDevCount') || 0).toLocaleString()}/
                  {(snapshot.get('licensedDevCount') || 0).toLocaleString()}
                </div>
              )}
              <div className='snapshots-content__detail-cell'>
                {getSnapshotSize(msg, snapshot.get('unloadedSize'))}
              </div>
            </div>
          </div>
        </div>
        {this.getSites()}
        <div className='mb-5'>
          <Note canEdit={canEdit} snapshot={snapshot} msg={msg} />
        </div>
        <CloudLicensingGuideGate
          isCloudLicensingOverviewAccessible={
            isCloudLicenseActive && isSnapshotLoaded(snapshot.get('state'))
          }
        />
      </Fragment>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  snapshot: makeSelectContentSnapshot(),
});

export default connect(mapStateToProps)(ContentDetail);
